import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from '../Text'
import Icon from '../icon/Icon'
import ValueBar from '../ValueBar'
import { ProgressBar } from '../ProgressBar'
import DeltaPill from '../insights/DeltaPill'
import ResultsHeader from '../ResultsHeader'
import { HoverText, IPointerPosition } from '../tag/JobTag'

// Utils
import { useAppState } from '../../context/AppContext'

// Styles
import { applyTextStyle } from '../../styles/typography'
import { fastTransition } from '../../styles/effects'
import { mqTo } from '../../styles/mediaQueries'

// Types
import { IconType } from '../icon/IconType'

export type OverviewMode = 'assessors' | 'results'

export interface IOverviewRow {
  id: string
  title: string
  // Small caps eyebrow + icon shown left of the title (e.g. the skill theme
  // or "VALUE" for culture). Falls back to the section icon when omitted.
  category?: string
  categoryColor?: string
  icon?: IconType
  // Per-row icon treatment (culture values get a round, value-colored icon).
  round?: boolean
  iconColor?: string
  iconBg?: string
  // Raw bar colors (culture values colour their score bar).
  barColor?: string
  trackColor?: string
  // Segmented bar (skills/culture). Falls back to a continuous fill when a
  // percentage is given instead. Goals carry a statusLabel (e.g. "Achieved")
  // instead of a bar.
  segments?: { total: number; complete: number }
  percentage?: number
  statusLabel?: string
  current?: number
  previous?: number
}

interface IProps {
  title: string
  icon: IconType
  // Left column header label key (e.g. the "skill"/"value"/"goal" caption).
  entityLabelKey: string
  resultsCount: number
  mode: OverviewMode
  onModeChange: (mode: OverviewMode) => void
  resultsDisabled?: boolean
  rows: IOverviewRow[]
  assessorsContent: React.ReactNode
  onRowClick: (id: string) => void
  className?: string
}

// One section ("Skills overview" / "Culture overview" / "Goals overview") with
// an Assessors | Results toggle. Results lists the entities (drillable rows);
// Assessors renders the injected assessor rows from EvaluationModal so that
// view stays a single source of truth.
const EvaluationOverviewList: React.FC<IProps> = ({
  title,
  icon,
  entityLabelKey,
  resultsCount,
  mode,
  onModeChange,
  resultsDisabled,
  rows,
  assessorsContent,
  onRowClick,
  className,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()

  // Custom tooltip (HoverText) for the view-toggle segments, tracked by pointer
  // position so it follows the cursor like the rest of the app's hover hints.
  const [tip, setTip] = useState<{ text: string; position: IPointerPosition }>()
  // Anchor the tip under the hovered segment (centre-bottom) rather than the
  // cursor, so it stays inside the viewport near the right edge.
  const showTip = (text: string, e: React.PointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect()
    setTip({
      text,
      position: {
        x: Math.round(r.left + r.width / 2 + window.scrollX),
        y: Math.round(r.bottom + window.scrollY),
      },
    })
  }
  const hideTip = () => setTip(undefined)

  // Goals render a final-score status instead of a bar; skills/culture render a
  // score bar with a "vs last" delta column.
  const isStatus = rows.some((row) => typeof row.statusLabel === 'string')

  // Drive column-title visibility off the real score/delta, not off `segments`:
  // skills/culture also carry an (empty) segments bar when they couldn't be
  // scored, so only `current`/`statusLabel` indicate an actual score.
  const hasScore = rows.some(
    (row) =>
      typeof row.current === 'number' ||
      typeof row.percentage === 'number' ||
      typeof row.statusLabel === 'string'
  )
  const hasDelta = rows.some((row) => typeof row.previous === 'number')

  return (
    <Section className={className}>
      <SectionHeader
        icon={icon}
        title={title}
        resultsLength={resultsCount}
        // Hide the toggle entirely when there are no results to switch to.
        rightContent={
          resultsDisabled ? undefined : (
            <ViewToggle role="tablist">
              <Segment
                type="button"
                $active={mode === 'assessors'}
                aria-label={t('termEvaluationsProgress.assessors')}
                aria-pressed={mode === 'assessors'}
                onClick={() => onModeChange('assessors')}
                onPointerEnter={(e) =>
                  showTip(t('termEvaluationsProgress.assessors'), e)
                }
                onPointerLeave={hideTip}
              >
                <Icon
                  type="People"
                  size={16}
                  color={
                    mode === 'assessors'
                      ? theme.colors.lightest
                      : theme.colors.textLight
                  }
                />
              </Segment>
              <Segment
                type="button"
                $active={mode === 'results'}
                aria-label={t('general.results')}
                aria-pressed={mode === 'results'}
                onClick={() => onModeChange('results')}
                onPointerEnter={(e) => showTip(t('general.results'), e)}
                onPointerLeave={hideTip}
              >
                <Icon
                  type="Statistics"
                  size={16}
                  color={
                    mode === 'results'
                      ? theme.colors.lightest
                      : theme.colors.textLight
                  }
                />
              </Segment>
              {tip && (
                <HoverText text={tip.text} position={tip.position} anchored />
              )}
            </ViewToggle>
          )
        }
      />

      {mode === 'results' ? (
        <>
          <ResultsBlock>
            <ColumnHeader>
              <ColLabel textStyle="labelSmallCaps" color="text72" tag="span">
                {t(entityLabelKey)}
              </ColLabel>
              <ColScore textStyle="labelSmallCaps" color="text72" tag="span">
                {hasScore &&
                  t(
                    isStatus
                      ? 'evaluationHeader.finalScore'
                      : 'evaluationModal.score'
                  )}
              </ColScore>
              {!isStatus && (
                <ColVsLast textStyle="labelSmallCaps" color="text72" tag="span">
                  {hasDelta && t('evaluationModal.vsLast')}
                </ColVsLast>
              )}
              <ColChevron />
            </ColumnHeader>

            <Rows>
              {rows.map((row) => {
                const hasRowDelta = typeof row.previous === 'number'
                return (
                  <Row
                    key={row.id}
                    $hasDelta={hasRowDelta}
                    onClick={() => onRowClick(row.id)}
                  >
                    <RowMain>
                      <IconBox $round={row.round} $bg={row.iconBg}>
                        <Icon
                          type={row.icon ?? icon}
                          size={16}
                          color={row.iconColor ?? theme.colors.primary}
                        />
                      </IconBox>
                      <RowText>
                        {row.category && (
                          <CategoryEyebrow
                            $color={row.categoryColor ?? theme.colors.primary}
                          >
                            {row.category}
                          </CategoryEyebrow>
                        )}
                        <Title textStyle="labelHeavy" color="text" tag="span">
                          {row.title}
                        </Title>
                      </RowText>
                    </RowMain>

                    {row.statusLabel ? (
                      <RowStatus
                        textStyle="labelHeavy"
                        color="primary"
                        tag="span"
                      >
                        {row.statusLabel}
                      </RowStatus>
                    ) : (
                      <RowBar>
                        {row.segments ? (
                          <ValueBar
                            total={row.segments.total}
                            complete={row.segments.complete}
                            height={8}
                            gap={4}
                            barColor={row.barColor}
                            trackColor={
                              row.trackColor ?? theme.colors.primaryFadeLight
                            }
                          />
                        ) : typeof row.percentage === 'number' ? (
                          <ProgressBar
                            percentage={row.percentage}
                            color={theme.colors.primary}
                            backgroundColor={theme.colors.lightest}
                          />
                        ) : null}
                      </RowBar>
                    )}

                    {!isStatus && (
                      <RowDelta $hasDelta={hasRowDelta}>
                        {hasRowDelta && (
                          <DeltaPill
                            current={row.current}
                            previous={row.previous}
                            size="small"
                          />
                        )}
                      </RowDelta>
                    )}

                    <ChevronIcon
                      type="ChevronRight"
                      size={12}
                      color={theme.colors.primaryFadeHalf}
                    />
                  </Row>
                )
              })}
            </Rows>
          </ResultsBlock>
        </>
      ) : (
        <AssessorsList>{assessorsContent}</AssessorsList>
      )}
    </Section>
  )
}

export default EvaluationOverviewList

// Shared column widths so the column header lines up with the rows.
const BAR_WIDTH = '164px'
const DELTA_WIDTH = '60px'
const CHEVRON_WIDTH = '16px'
const ROW_GAP = '24px'
const ROW_PADDING_X = '8px'

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

// Reuse the compact pill header (icon + UPPERCASE title + count badge); the
// Section flex gap handles spacing, so drop the component's own margin.
const SectionHeader = styled(ResultsHeader)`
  margin: 0;
`

// Discreet segmented icon toggle docked on the right of the header pill. The
// white container lifts it off the lavender header background.
const ViewToggle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border-radius: 8px;
  flex-shrink: 0;
  background-color: ${({ theme }) => theme.colors.lightest};
`

const Segment = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  transition: background-color ${fastTransition};
  background-color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : 'transparent'};
  /* The active segment can't be re-selected, so it isn't interactive. */
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};

  > span {
    margin-right: 0;
  }

  /* Only the inactive segment reacts to hover: a faint fill plus the icon
     previewing the selected colour (the fill is a presentation attribute on
     the SVG path, so CSS can override it). */
  ${({ theme, $active }) =>
    !$active &&
    css`
      &:hover {
        background-color: ${theme.colors.primaryFadeLight};

        path {
          fill: ${theme.colors.primary};
        }
      }
    `}
`

// Small caps eyebrow above the row title; colour comes per row (e.g. the
// culture value colour), falling back to primary.
const CategoryEyebrow = styled.span<{ $color: string }>`
  ${applyTextStyle('labelSmallCaps')}
  color: ${({ $color }) => $color};
`

// Plain wrapper so the assessor list keeps its own row spacing instead of
// inheriting the Section's flex gap (matches the pre-revamp list spacing).
const AssessorsList = styled.div``

const ColumnHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${ROW_GAP};
  padding: 0 ${ROW_PADDING_X};

  > span {
    margin-right: 0;
  }

  /* Mobile cards are self-describing, so drop the SKILL/SCORE/VS LAST header. */
  ${mqTo.M`
    display: none;
  `}
`

const ColLabel = styled(Text)`
  flex: 1;
  min-width: 0;
`

const ColScore = styled(Text)`
  flex: 0 0 ${BAR_WIDTH};
`

const ColVsLast = styled(Text)`
  flex: 0 0 ${DELTA_WIDTH};
`

const ColChevron = styled.div`
  flex: 0 0 ${CHEVRON_WIDTH};
`

// Column header sits tight above the rows; the 12px Section gap spaces the
// header/divider away from this block.
const ResultsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  /* Give the mobile cards some breathing room between them. */
  ${mqTo.M`
    gap: 12px;
  `}
`

// Chevron defaults to primary @ 50%; on row hover it goes full primary. The
// fill is a presentation attribute on the SVG path, so CSS can override it.
const ChevronIcon = styled(Icon)`
  ${mqTo.M`
    grid-area: chevron;
    justify-self: end;
  `}
`

const Row = styled.div<{ $hasDelta?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${ROW_GAP};
  padding: ${ROW_PADDING_X};
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};

    ${ChevronIcon} path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }

  > span:last-child {
    margin-right: 0;
  }

  /* On mobile the fixed columns leave no room for the title, so each row becomes
     a card: the title shares the first line with the chevron, and the score bar
     shares the second line with the delta. Without a delta the bar spans both
     columns so it always fills the full width. */
  ${mqTo.M`
    ${({ $hasDelta, theme }) => css`
      display: grid;
      grid-template-columns: 1fr auto;
      grid-template-areas: ${$hasDelta
        ? `'main chevron' 'bar delta'`
        : `'main chevron' 'bar bar'`};
      align-items: center;
      column-gap: 10px;
      row-gap: 10px;
      padding: 12px;
      border-radius: 12px;
      border: 1px solid ${theme.colors.textLighter};
    `}
  `}
`

const RowMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;

  ${mqTo.M`
    grid-area: main;
  `}
`

const IconBox = styled.div<{ $round?: boolean; $bg?: string }>`
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ $round }) => ($round ? '50%' : '8px')};
  border: 1px solid ${({ theme, $bg }) => $bg ?? theme.colors.textLightest};
  background-color: ${({ theme, $bg }) => $bg ?? theme.colors.primaryLight};

  > span {
    margin-right: 0;
  }
`

const RowText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  > span {
    margin-right: 0;
  }
`

const Title = styled(Text)`
  overflow-wrap: anywhere;
`

const RowBar = styled.div`
  flex: 0 0 ${BAR_WIDTH};

  ${mqTo.M`
    grid-area: bar;
    min-width: 0;
  `}
`

const RowStatus = styled(Text)`
  flex: 0 0 ${BAR_WIDTH};

  ${mqTo.M`
    grid-area: bar;
  `}
`

const RowDelta = styled.div<{ $hasDelta?: boolean }>`
  flex: 0 0 ${DELTA_WIDTH};
  display: flex;

  ${mqTo.M`
    grid-area: delta;
    justify-self: end;
  `}

  /* No delta on mobile: collapse the cell so it can't add an empty grid row. */
  ${({ $hasDelta }) =>
    !$hasDelta &&
    mqTo.M`
      display: none;
    `}
`
